/**
 * Virtualized product list (feature #4) using react-window. Sections are
 * flattened into a single row model of header rows and product rows so a
 * VariableSizeList can render only what's on screen — important for large
 * categories.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { VariableSizeList, type ListChildComponentProps } from 'react-window';
import type { Product } from '@mitang/shared';
import { ProductCard } from './ProductCard';
import { groupBySection } from './search';
import { useScrollRestore } from '../../hooks/useScrollRestore';

type Row =
  | { kind: 'header'; title: string }
  | { kind: 'product'; product: Product };

const HEADER_HEIGHT = 38;
const CARD_HEIGHT = 176; // Showroom card + gap
const CARD_HEIGHT_PRICED = 212; // …with a price row

interface Props {
  products: Product[];
  recentlyUpdated: Set<string>;
  onOpen: (product: Product) => void;
  height: number;
  /** productId → price columns (allow-listed users only). */
  prices?: Map<string, Array<{ k: string; v: string }>>;
  savedIds: Set<string>;
  onToggleSaved: (product: Product) => void;
  /** Remembers the scroll position under this key (e.g. `home:<category>`). */
  scrollKey?: string;
}

interface RowData {
  rows: Row[];
  recentlyUpdated: Set<string>;
  onOpen: (product: Product) => void;
  prices?: Map<string, Array<{ k: string; v: string }>>;
  savedIds: Set<string>;
  onToggleSaved: (product: Product) => void;
}

function RowRenderer({ index, style, data }: ListChildComponentProps<RowData>): JSX.Element {
  const row = data.rows[index];
  if (row.kind === 'header') {
    return (
      <div style={style} className="flex items-end px-4 pb-1 pt-3">
        <h2 className="text-[10px] font-bold uppercase tracking-[.16em] text-slate-400">{row.title}</h2>
      </div>
    );
  }
  return (
    <div style={style} className="px-4 pb-3" data-scroll-anchor={row.product.id}>
      <ProductCard
        product={row.product}
        recentlyUpdated={data.recentlyUpdated.has(row.product.id)}
        onOpen={data.onOpen}
        priceAttrs={data.prices?.get(row.product.id)}
        saved={data.savedIds.has(row.product.id)}
        onToggleSaved={data.onToggleSaved}
      />
    </div>
  );
}

export function ProductList({
  products,
  recentlyUpdated,
  onOpen,
  height,
  prices,
  savedIds,
  onToggleSaved,
  scrollKey,
}: Props): JSX.Element {
  const listRef = useRef<VariableSizeList>(null);
  const outerRef = useRef<HTMLDivElement>(null);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const section of groupBySection(products)) {
      if (section.title) out.push({ kind: 'header', title: section.title });
      for (const p of section.products) out.push({ kind: 'product', product: p });
    }
    return out;
  }, [products]);

  const hasPrices = !!prices && prices.size > 0;
  const getItemSize = useCallback(
    (index: number) =>
      rows[index].kind === 'header'
        ? HEADER_HEIGHT
        : hasPrices
          ? CARD_HEIGHT_PRICED
          : CARD_HEIGHT,
    [rows, hasPrices],
  );

  // Coming back from a product returns to the same spot, tapped card in view.
  const rememberAnchor = useScrollRestore(scrollKey, outerRef, rows.length > 0, {
    scrollToAnchor: (id) => {
      const index = rows.findIndex((r) => r.kind === 'product' && r.product.id === id);
      if (index < 0) return false;
      listRef.current?.scrollToItem(index, 'center');
      return true;
    },
  });
  const openAndRemember = useCallback(
    (product: Product) => {
      rememberAnchor(product.id);
      onOpen(product);
    },
    [rememberAnchor, onOpen],
  );

  const itemData = useMemo<RowData>(
    () => ({ rows, recentlyUpdated, onOpen: openAndRemember, prices, savedIds, onToggleSaved }),
    [rows, recentlyUpdated, openAndRemember, prices, savedIds, onToggleSaved],
  );

  // Re-measure when price rows appear/disappear.
  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [hasPrices, rows]);

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-10 text-sm text-slate-400">
        No products match your search.
      </div>
    );
  }

  return (
    <VariableSizeList
      ref={listRef}
      outerRef={outerRef}
      height={height}
      width="100%"
      itemCount={rows.length}
      itemSize={getItemSize}
      itemKey={(index) => {
        const r = rows[index];
        return r.kind === 'header' ? `h:${r.title}` : `p:${r.product.id}`;
      }}
      itemData={itemData}
      overscanCount={4}
    >
      {RowRenderer}
    </VariableSizeList>
  );
}
